/**
 * <content-drawers>
 * -----------------
 * Controller for storewide lightweight info drawers + explainer variants.
 *
 * Responsibilities:
 *   1. Listen (via delegation) for clicks on any [data-drawer-trigger]
 *   2. On trigger, clone the matching <template> into the shared <dialog>
 *   3. Apply the template's variant + height preset to the dialog attributes
 *      (and forward a custom height value as a CSS variable when preset=custom)
 *   4. Call dialog.showModal() — browser handles focus trap, Escape, inert
 *   5. Wire up drag-to-dismiss on mobile (pointer events)
 *   6. Wire up HSA explainer tabs (ARIA pattern: arrows, Home/End)
 *   7. Close on backdrop click, close button, Escape, drag threshold
 *   8. Return focus to triggering element on close (WCAG 2.4.3)
 *
 * Accessibility:
 *   - Upgrades non-<button> triggers at runtime (role, tabindex, keyboard)
 *   - Respects prefers-reduced-motion (skips close animation)
 *   - Native <dialog> provides modal semantics, focus trap, inert background
 *   - Drag is enhancement only; every path has a keyboard equivalent
 *   - Explainer tabs follow W3C ARIA Authoring Practices tabs pattern
 *     (roving tabindex — only the active tab is in the tab sequence)
 */

(function () {
  'use strict';

  const DESKTOP_BREAKPOINT = 768;

  // Drag: close if pulled down > 25% of panel height, or velocity > 0.5 px/ms
  const DRAG_DISMISS_THRESHOLD = 0.25;
  const DRAG_DISMISS_VELOCITY = 0.5;

  // Validation bounds for the custom height value (in dvh).
  // Mirrors the schema range (30–95) but defended again at runtime in case
  // a future schema change or hand-edited block.settings leaks an out-of-range
  // value into the rendered template.
  const CUSTOM_HEIGHT_MIN = 30;
  const CUSTOM_HEIGHT_MAX = 95;

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const isDesktop = () => window.innerWidth >= DESKTOP_BREAKPOINT;

  /**
   * Parse + sanitize the custom-height attribute (e.g. "70dvh", "85dvh").
   * Returns a safe CSS length string, or null if the value is invalid.
   *
   * Why we sanitize on the client side too:
   *   - The value already passes through Liquid validation, but the JS path
   *     writes directly to element.style. A defensive parse here prevents
   *     any future template change (or hand-edited settings JSON) from
   *     injecting unexpected CSS syntax into an inline style attribute.
   *   - Keeps the contract explicit: only a numeric value + dvh/vh/% is
   *     accepted, all clamped to the documented 30–95 range.
   */
  const parseCustomHeight = (raw) => {
    if (!raw || typeof raw !== 'string') return null;
    // Accept formats: "70dvh", "70vh", "70%", or plain "70" (assume dvh).
    const match = raw.trim().match(/^(\d+(?:\.\d+)?)(dvh|vh|%)?$/i);
    if (!match) return null;
    let value = parseFloat(match[1]);
    if (!Number.isFinite(value)) return null;
    if (value < CUSTOM_HEIGHT_MIN) value = CUSTOM_HEIGHT_MIN;
    if (value > CUSTOM_HEIGHT_MAX) value = CUSTOM_HEIGHT_MAX;
    const unit = (match[2] || 'dvh').toLowerCase();
    return value + unit;
  };

  class ContentDrawers extends HTMLElement {
    constructor() {
      super();
      this.dialog = null;
      this.panel = null;
      this.grabber = null;
      this.contentTarget = null;
      this.labelTarget = null;
      this.templates = new Map();
      this.lastTrigger = null;
      this.dragState = null;

      // Active explainer refs (reset on each open)
      this.activeExplainer = null;

      this._onDocClick = this._onDocClick.bind(this);
      this._onDocKeydown = this._onDocKeydown.bind(this);
      this._onDialogClick = this._onDialogClick.bind(this);
      this._onDialogClose = this._onDialogClose.bind(this);
      this._onDialogCancel = this._onDialogCancel.bind(this);
      this._onPointerDown = this._onPointerDown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);
      this._onDialogTouchMove = this._onDialogTouchMove.bind(this);
      this._onTabClick = this._onTabClick.bind(this);
      this._onTabKeydown = this._onTabKeydown.bind(this);
      this._onDialogKeydown = this._onDialogKeydown.bind(this);
    }

    connectedCallback() {
      this.dialog = this.querySelector('[data-content-drawer]');
      if (!this.dialog) return;

      this.panel = this.dialog.querySelector('[data-drawer-panel]');
      this.grabber = this.dialog.querySelector('[data-drawer-grabber]');
      this.contentTarget = this.dialog.querySelector('[data-drawer-content]');
      this.labelTarget = this.dialog.querySelector('[data-drawer-label]');

      this.querySelectorAll('.content-drawer-template').forEach((tpl) => {
        const handle = tpl.getAttribute('data-drawer-handle');
        if (handle) this.templates.set(handle, tpl);
      });

      this._upgradeTriggers(document);

      document.addEventListener('click', this._onDocClick);
      document.addEventListener('keydown', this._onDocKeydown);

      this.dialog.addEventListener('click', this._onDialogClick);
      this.dialog.addEventListener('close', this._onDialogClose);
      this.dialog.addEventListener('cancel', this._onDialogCancel);
      this.dialog.addEventListener('keydown', this._onDialogKeydown);
      // Non-passive so we can preventDefault on attempted page-through scroll.
      this.dialog.addEventListener('touchmove', this._onDialogTouchMove, { passive: false });

      if (this.panel) {
        this.panel.addEventListener('pointerdown', this._onPointerDown);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocClick);
      document.removeEventListener('keydown', this._onDocKeydown);
      if (this.dialog) {
        this.dialog.removeEventListener('click', this._onDialogClick);
        this.dialog.removeEventListener('close', this._onDialogClose);
        this.dialog.removeEventListener('cancel', this._onDialogCancel);
        this.dialog.removeEventListener('keydown', this._onDialogKeydown);
        this.dialog.removeEventListener('touchmove', this._onDialogTouchMove);
      }
    }

    /* --------------------------------------------------------------
       Trigger handling (event delegation — storewide)
       -------------------------------------------------------------- */

    _onDocClick(event) {
      const trigger = event.target.closest('[data-drawer-trigger]');
      if (!trigger) return;

      const handle = trigger.getAttribute('data-drawer-trigger');
      if (!handle || !this.templates.has(handle)) return;

      event.preventDefault();
      this.open(handle, trigger);
    }

    _onDocKeydown(event) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const trigger = event.target.closest('[data-drawer-trigger]');
      if (!trigger || trigger.tagName === 'BUTTON') return;

      const handle = trigger.getAttribute('data-drawer-trigger');
      if (!handle || !this.templates.has(handle)) return;

      event.preventDefault();
      this.open(handle, trigger);
    }

    _upgradeTriggers(root) {
      const triggers = root.querySelectorAll(
        '[data-drawer-trigger]:not(button):not([data-drawer-upgraded])'
      );
      triggers.forEach((el) => {
        el.setAttribute('data-drawer-upgraded', '');
        if (el.tagName === 'A' && el.hasAttribute('href')) return;
        if (!el.hasAttribute('role')) el.setAttribute('role', 'button');
        // RESTORED: non-button triggers must be in the tab sequence to be
        // keyboard-activatable. Without this, Enter/Space keydown handlers
        // above never fire because the element never receives focus.
        if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');
      });
    }

    /* --------------------------------------------------------------
       Open / close
       -------------------------------------------------------------- */

    open(handle, trigger) {
      const tpl = this.templates.get(handle);
      if (!tpl || !this.dialog) return;

      this.lastTrigger = trigger || document.activeElement;

      // Apply variant + height to the dialog so CSS can size it before show
      const variant = tpl.getAttribute('data-drawer-variant') || 'info';
      const height = tpl.getAttribute('data-drawer-height') || '';
      this.dialog.setAttribute('data-drawer-variant', variant);
      if (height) {
        this.dialog.setAttribute('data-drawer-height', height);
      } else {
        this.dialog.removeAttribute('data-drawer-height');
      }

      // Mirror the drawer handle onto the dialog so per-drawer CSS can target
      // it without needing to inspect the cloned template content. Required
      // for merchant/dev-authored styles like:
      //   .content-drawer[data-drawer-handle="memorial-day-sale"] { ... }
      // The handle itself comes from the `<template data-drawer-handle="...">`
      // (which is the same string used in `data-drawer-trigger`), so we use
      // the lookup `handle` argument directly — already validated by the
      // templates.get() check above.
      this.dialog.setAttribute('data-drawer-handle', handle);

      // Custom height pass-through.
      // When the merchant picks "Custom" in the schema, the template carries
      // a `data-drawer-custom-height` attribute (e.g. "70dvh"). We mirror it
      // onto the dialog as a CSS variable so the stylesheet — which only sees
      // attribute selectors — can resolve `data-drawer-height="custom"` to a
      // concrete length without any extra DOM. The value is sanitized; if it
      // fails validation we strip the variable so the CSS falls back to its
      // built-in default for the custom preset.
      const rawCustom = tpl.getAttribute('data-drawer-custom-height');
      if (height === 'custom' && rawCustom) {
        const safeCustom = parseCustomHeight(rawCustom);
        if (safeCustom) {
          this.dialog.style.setProperty('--drawer-custom-height', safeCustom);
        } else {
          this.dialog.style.removeProperty('--drawer-custom-height');
        }
      } else {
        this.dialog.style.removeProperty('--drawer-custom-height');
      }

      // Update accessible name
      const headline = tpl.getAttribute('data-drawer-headline') || '';
      if (this.labelTarget) this.labelTarget.textContent = headline;

      // Swap content
      if (this.contentTarget) {
        this.contentTarget.replaceChildren();
        this.contentTarget.appendChild(tpl.content.cloneNode(true));
        // Reset scroll position for explainer variant
        this.contentTarget.scrollTop = 0;
      }

      // Reset state
      this.panel.style.setProperty('--drag-y', '0px');
      this.dialog.removeAttribute('data-closing');
      this.dialog.removeAttribute('data-dragging');

      // Variant-specific wiring
      if (variant === 'explainer') {
        this._wireExplainer();
      }

      try {
        this.dialog.showModal();
      } catch (_err) {
        this.dialog.close();
        this.dialog.showModal();
      }
    }

    close() {
      if (!this.dialog || !this.dialog.open) return;

      if (prefersReducedMotion()) {
        this.dialog.close();
        return;
      }

      this.dialog.setAttribute('data-closing', 'true');
      const onEnd = (event) => {
        if (event && event.target !== this.panel) return;
        this.panel.removeEventListener('animationend', onEnd);
        this.dialog.close();
        this.dialog.removeAttribute('data-closing');
      };
      this.panel.addEventListener('animationend', onEnd);

      setTimeout(() => {
        if (this.dialog.hasAttribute('data-closing')) onEnd();
      }, 300);
    }

    _onDialogClick(event) {
      if (event.target.closest('[data-drawer-close]')) {
        this.close();
        return;
      }
      if (event.target === this.dialog) {
        this.close();
      }
    }

    _onDialogClose() {
      this.panel.style.setProperty('--drag-y', '0px');
      this.dialog.removeAttribute('data-dragging');
      this.dialog.removeAttribute('data-drawer-variant');
      this.dialog.removeAttribute('data-drawer-height');
      this.dialog.removeAttribute('data-drawer-handle');
      // Clear the custom height variable so the next open starts clean.
      // Without this, a subsequent open of a non-custom drawer would still
      // have a stale `--drawer-custom-height` set on the dialog. The CSS
      // wouldn't read it (the data-drawer-height selector wouldn't match),
      // but cleaning up keeps inspector output honest and prevents surprises
      // if the CSS ever starts using the variable in additional selectors.
      this.dialog.style.removeProperty('--drawer-custom-height');

      // Tear down explainer wiring
      this._unwireExplainer();

      // Focus return — WCAG 2.4.3
      if (this.lastTrigger && typeof this.lastTrigger.focus === 'function') {
        try {
          this.lastTrigger.focus({ preventScroll: true });
        } catch (_) {
          this.lastTrigger.focus();
        }
      }
      this.lastTrigger = null;

      if (this.contentTarget) this.contentTarget.replaceChildren();
      if (this.labelTarget) this.labelTarget.textContent = '';
    }

    _onDialogCancel(event) {
      if (prefersReducedMotion()) return;
      event.preventDefault();
      this.close();
    }

    /* --------------------------------------------------------------
       Focus trap (required — native <dialog> trap has edge cases)
       --------------------------------------------------------------
       <dialog> + showModal() correctly pulls focus inside on open,
       but the native Tab-trap has edge cases that leak focus out:

         - When the last focusable element inside the dialog is a
           non-form-control with tabindex="0" (like our tabpanel),
           Safari in particular can let Tab escape to the document
           rather than wrap back to the first focusable element.
         - aria-hidden subtrees and display:none descendants can
           confuse the browser's internal focusable-list calculation.
         - Embedded webviews and older browsers have weaker traps.

       This manual trap catches Tab / Shift+Tab inside the dialog and
       wraps focus between the first and last VISIBLE focusable
       descendants. It runs on every Tab keypress while the dialog
       is open — cheap, reliable, and deterministic across browsers.
    */
    _getFocusableElements() {
      if (!this.dialog) return [];
      const selector = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ].join(',');

      // Only elements that are actually visible and not aria-hidden
      return Array.from(this.dialog.querySelectorAll(selector)).filter((el) => {
        if (el.closest('[aria-hidden="true"]')) return false;
        if (el.hasAttribute('disabled')) return false;
        // getClientRects is empty for display:none / visibility:hidden
        if (!el.getClientRects().length) return false;
        return true;
      });
    }

    _onDialogKeydown(event) {
      if (event.key !== 'Tab') return;
      if (!this.dialog || !this.dialog.open) return;

      const focusable = this._getFocusableElements();
      if (!focusable.length) {
        // Nothing focusable — keep focus on the dialog itself
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      // If focus is somehow outside the dialog entirely, pull it back in.
      // This is the case that caused the browser-URL-bar escape: focus
      // was never inside the dialog in the first place, so the native
      // trap had nothing to contain.
      if (!this.dialog.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* --------------------------------------------------------------
       HSA/FSA Explainer — tabs pattern (ARIA Authoring Practices)
       --------------------------------------------------------------
       Keyboard model:
         - Left / Right arrows  → move selection within the tablist
         - Home / End           → jump to first / last tab
         - Tab key              → moves focus out of tablist into active panel
       Activation model: automatic (selection follows focus),
       consistent with the original mockup's simple two-tab UX.

       Focus containment:
         - Roving tabindex on tabs (only active tab has tabindex=0)
         - Only the ACTIVE panel has tabindex=0; inactive panel is
           tabindex=-1. This is what keeps Tab from escaping the modal
           via the hidden inactive panel.
    */

    _wireExplainer() {
      const explainer = this.contentTarget.querySelector('[data-hsa-explainer]');
      if (!explainer) return;

      const tabs = Array.from(explainer.querySelectorAll('[data-hsa-tab]'));
      const panels = Array.from(explainer.querySelectorAll('[data-hsa-panel]'));
      const announcer = explainer.querySelector('[data-hsa-announcer]');

      if (!tabs.length || !panels.length) return;

      this.activeExplainer = {
        root: explainer,
        tabs,
        panels,
        announcer,
      };

      // Always reset to first tab on open (also normalizes tabindex state)
      this._activateHsaTab(tabs[0], { focusTab: false, announce: false });

      tabs.forEach((tab) => {
        tab.addEventListener('click', this._onTabClick);
        tab.addEventListener('keydown', this._onTabKeydown);
      });
    }

    _unwireExplainer() {
      if (!this.activeExplainer) return;
      this.activeExplainer.tabs.forEach((tab) => {
        tab.removeEventListener('click', this._onTabClick);
        tab.removeEventListener('keydown', this._onTabKeydown);
      });
      this.activeExplainer = null;
    }

    _onTabClick(event) {
      const tab = event.currentTarget;
      this._activateHsaTab(tab, { focusTab: true, announce: true });
    }

    _onTabKeydown(event) {
      if (!this.activeExplainer) return;
      const { tabs } = this.activeExplainer;
      const currentIdx = tabs.indexOf(event.currentTarget);
      if (currentIdx === -1) return;

      let nextIdx = null;
      switch (event.key) {
        case 'ArrowRight':
          nextIdx = (currentIdx + 1) % tabs.length;
          break;
        case 'ArrowLeft':
          nextIdx = (currentIdx - 1 + tabs.length) % tabs.length;
          break;
        case 'Home':
          nextIdx = 0;
          break;
        case 'End':
          nextIdx = tabs.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      this._activateHsaTab(tabs[nextIdx], { focusTab: true, announce: true });
    }

    _activateHsaTab(tab, opts) {
      if (!this.activeExplainer) return;
      const { tabs, panels, announcer } = this.activeExplainer;
      const options = opts || {};

      // Roving tabindex on TABS — critical for focus containment.
      // Only the active tab participates in the tab sequence.
      tabs.forEach((t) => {
        const isActive = t === tab;
        t.classList.toggle('is-active', isActive);
        t.setAttribute('aria-selected', isActive ? 'true' : 'false');
        t.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      // Only the active PANEL is focusable. The inactive panel is
      // display:none, but without tabindex=-1 here some browsers still
      // count it as focusable during tab-order calculation, which is
      // how Tab was appearing to "escape" the modal.
      const targetPanelId = tab.getAttribute('aria-controls');
      panels.forEach((p) => {
        const isActive = p.id === targetPanelId;
        p.classList.toggle('is-active', isActive);
        p.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      if (options.focusTab) {
        tab.focus();
      }

      if (options.announce && announcer) {
        announcer.textContent = tab.textContent.trim() + ' tab selected.';
      }
    }

    /* --------------------------------------------------------------
       Drag to dismiss
       --------------------------------------------------------------
       For explainer variant we avoid hijacking drags that originate
       inside the scrollable content area or inside interactive UI
       (tabs, links). The grabber area is always draggable.
    */

    _onPointerDown(event) {
      // Only react on mobile-ish viewports.
      if (isDesktop() && !window.matchMedia('(pointer: coarse)').matches) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      // Don't start drag on interactive elements
      if (event.target.closest('a, button, [role="button"], [role="tab"]')) return;

      // For explainer variant: don't start a drag from inside the scroll area
      // unless it's from the grabber. The scroll area owns its own pan gesture.
      const variant = this.dialog.getAttribute('data-drawer-variant');
      if (variant === 'explainer') {
        const isGrabber = !!event.target.closest('[data-drawer-grabber]');
        const isInContent = !!event.target.closest('[data-drawer-content]');
        if (isInContent && !isGrabber) return;
      }

      const panelRect = this.panel.getBoundingClientRect();
      this.dragState = {
        startY: event.clientY,
        startX: event.clientX,
        startTime: performance.now(),
        lastY: event.clientY,
        lastTime: performance.now(),
        panelHeight: panelRect.height,
        pointerId: event.pointerId,
        committed: false,
      };

      window.addEventListener('pointermove', this._onPointerMove, { passive: false });
      window.addEventListener('pointerup', this._onPointerUp);
      window.addEventListener('pointercancel', this._onPointerUp);
    }

    _onPointerMove(event) {
      if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;

      const dy = event.clientY - this.dragState.startY;
      const dx = event.clientX - this.dragState.startX;

      if (!this.dragState.committed) {
        const moved = Math.hypot(dx, dy);
        if (moved < 5) return;
        if (Math.abs(dx) > Math.abs(dy)) {
          this._endDrag(false);
          return;
        }
        this.dragState.committed = true;
        this.dialog.setAttribute('data-dragging', 'true');
      }

      if (event.cancelable) event.preventDefault();

      const translated = dy > 0 ? dy : dy * 0.2;
      this.panel.style.setProperty('--drag-y', `${translated}px`);

      this.dragState.lastY = event.clientY;
      this.dragState.lastTime = performance.now();
    }

    _onPointerUp(event) {
      if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;

      const committed = this.dragState.committed;
      const { startY, startTime, lastY, lastTime, panelHeight } = this.dragState;

      this._endDrag(committed);

      if (!committed) return;

      const totalDy = lastY - startY;
      const elapsed = Math.max(1, lastTime - startTime);
      const velocity = totalDy / elapsed;

      const dismissByDistance = totalDy > panelHeight * DRAG_DISMISS_THRESHOLD;
      const dismissByVelocity = velocity > DRAG_DISMISS_VELOCITY && totalDy > 20;

      if (dismissByDistance || dismissByVelocity) {
        this.close();
      } else {
        this.panel.style.setProperty('--drag-y', '0px');
      }
    }

    _endDrag(wasCommitted) {
      window.removeEventListener('pointermove', this._onPointerMove);
      window.removeEventListener('pointerup', this._onPointerUp);
      window.removeEventListener('pointercancel', this._onPointerUp);

      this.dragState = null;
      this.dialog.removeAttribute('data-dragging');

      if (!wasCommitted) {
        this.panel.style.setProperty('--drag-y', '0px');
      }
    }

    /* --------------------------------------------------------------
       Scroll-through prevention
       --------------------------------------------------------------
       For info variant: block all backdrop touchmove so page doesn't scroll.
       For explainer variant: block only backdrop; let the scrollable content
       area receive its touch gestures naturally.
    */

    _onDialogTouchMove(event) {
      if (!this.dialog || !this.dialog.open) return;

      // Block only when the touch is on the dialog itself (backdrop).
      // Touches inside the panel (grabber, scrollable content, tabs, etc.)
      // are handled by their respective handlers or allowed to scroll.
      if (event.target === this.dialog) {
        if (event.cancelable) event.preventDefault();
      }
    }
  }

  if (!customElements.get('content-drawers')) {
    customElements.define('content-drawers', ContentDrawers);
  }
})();